import React, { useState } from "react";
import { DateTime } from "luxon";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";


import {
    GanttDragItem,
    OdPDragItem,
    FaseBarDragItem,
    FrazioneBarDragItem,
    VariazioneBarDragItem,
    isOdPDragItem,
    isFaseBarDragItem,
    isFrazioneBarDragItem,
    isVariazioneNewDragItem,
    isVariazioneBarDragItem,
    ACCEPTED_DROP_TYPES,
} from "./schedulazioneGanttTypes";
import DnDDroppableBox from "../../../common/DnD/DnDDroppableBox";

// ============================================
// INTERFACES
// ============================================

interface GanttDropCellProps {
    giorno: DateTime;
    onOdPDrop: (odpId: number, data: string) => void;
    onFaseMove: (draggedItem: FaseBarDragItem, nuovaData: string) => void;
    onFaseResize: (draggedItem: FaseBarDragItem, nuovaData: string) => void;
    onFrazioneMove: (frazioneId: number, nuovaDataInizio: string) => void;
    onVariazioneCreate?: (data: string) => void;
    onVariazioneMove?: (draggedItem: VariazioneBarDragItem, nuovaData: string) => void;
    onVariazioneResize?: (draggedItem: VariazioneBarDragItem, nuovaData: string) => void;
    minHeight?: number;
    isLavorativo?: boolean;
}

// ============================================
// COMPONENT
// ============================================

const GanttDropCell = ({
                           giorno,
                           onOdPDrop,
                           onFaseMove,
                           onFaseResize,
                           onFrazioneMove,
                           onVariazioneCreate,
                           onVariazioneMove,
                           onVariazioneResize,
                           minHeight = 60,
                           isLavorativo = true,
                       }: GanttDropCellProps) => {
    const theme = useTheme();
    const [isOver, setIsOver] = useState(false);

    const isOggi = giorno.hasSame(DateTime.now(), "day");

    const getBackgroundColor = () => {
        if (!isLavorativo) {
            return theme.palette.grey[200];
        }
        if (isOver) {
            return theme.palette.success.light + "80";
        }
        if (isOggi) {
            return theme.palette.primary.light + "40";
        }
        return "transparent";
    };

    const handleDrop = (draggedItem: GanttDragItem) => {
        setIsOver(false);
        if (!isLavorativo) return;

        const nuovaData = giorno.toFormat("yyyy-MM-dd");

        // Drop di OdP dalla sidebar → crea nuova frazione
        if (isOdPDragItem(draggedItem)) {
            const odpItem = draggedItem as OdPDragItem;
            onOdPDrop(odpItem.odpId, nuovaData);
            return;
        }

        // Drag di fase-barra esistente
        if (isFaseBarDragItem(draggedItem)) {
            const faseItem = draggedItem as FaseBarDragItem;

            if (faseItem.resizeEdge) {
                onFaseResize(faseItem, nuovaData);
            } else {
                onFaseMove(faseItem, nuovaData);
            }
            return;
        }

        // Drag di frazione intera (slittamento)
        if (isFrazioneBarDragItem(draggedItem)) {
            const frazioneItem = draggedItem as FrazioneBarDragItem;
            onFrazioneMove(frazioneItem.frazioneId, nuovaData);
            return;
        }

        // Drop di nuovo placeholder dalla sidebar
        if (isVariazioneNewDragItem(draggedItem)) {
            onVariazioneCreate?.(nuovaData);
            return;
        }

        // Drag di placeholder esistente (move o resize)
        if (isVariazioneBarDragItem(draggedItem)) {
            const item = draggedItem as VariazioneBarDragItem;
            if (item.resizeEdge) {
                onVariazioneResize?.(item, nuovaData);
            } else {
                onVariazioneMove?.(item, nuovaData);
            }
        }
    };

    return (
        <DnDDroppableBox
            value={{ data: giorno.toISODate() }}
            accept={ACCEPTED_DROP_TYPES}
            showDropIndicator={false}
            onDragEnter={() => setIsOver(true)}
            onDragLeave={() => setIsOver(false)}
            onDrop={handleDrop}
        >
            <Box
                sx={{
                    borderRight: `1px solid ${theme.palette.divider}`,
                    backgroundColor: getBackgroundColor(),
                    minHeight,
                    height: "100%",
                    transition: "background-color 0.15s ease",
                }}
            />
        </DnDDroppableBox>
    );
};

export default GanttDropCell;